import {ErrorHandler, Inject, Injectable, isDevMode, OnDestroy} from '@angular/core';
import {cleanSubscription, ConfigService, LocaleService, PLAYER_CONFIG, PlayerConfig} from '@transparent/coretypes';
import {fromError} from 'stacktrace-js';
import {PathLocationStrategy} from '@angular/common';
import {HttpClient} from '@angular/common/http';
import {Observable, of, Subscription} from 'rxjs';
import {catchError, map, mergeMap} from 'rxjs/operators';

@Injectable()
export class PlayerErrorHandler extends ErrorHandler implements OnDestroy {
    private baseApiUrl = '';
    private httpSub: Subscription;

    constructor(@Inject(PLAYER_CONFIG) public upConfig: PlayerConfig,
                public locale: LocaleService,
                private config: ConfigService,
                private http: HttpClient) {
        super();
        this.baseApiUrl = (window as any).loxy || '//utilite1.transparent.local:4900';
    }

    ngOnDestroy(): void {
        this.cleanSub();
    }

    /**
     * error
     */
    handleError(error): void {
        const message: string = error ? error.message ? error.message : error.toString() : '';
        if (!message || message.includes('NG0100')) {
            return;
        }
        if (isDevMode() || message.includes('Http failure ')) {
            console.log(error);
            return;
        }
        const url = location instanceof PathLocationStrategy ? location.path() : '';
        const data = this.getLogData(error.message, url + '\n' + message);
        if (!error.hasOwnProperty('status')) {
            try {
                fromError(error)
                    .then(stackframes => {
                        data.error = stackframes.splice(0, 20).map((sf) => sf.toString()).join('\n');
                        this.sendLogs(data);
                    }, () => {
                        data.error = error.message;
                        this.sendLogs(data);
                    });
            } catch (e) {
                data.error = error.message;
                this.sendLogs(data);
            }
        } else {
            this.sendLogs(data);
        }
    }

    sendLogs(log: any = null): void {
        console.log(JSON.stringify(log));
        log = log || this.getLogData();
        if (this.upConfig.languageSelector) {
            log.langPair = 'known:' + this.upConfig.languageSelector.known + ',target:' + this.upConfig.languageSelector.target;
        } else if (this.config.learningLanguageCode) {
            log.langPair = 'known:' + this.config.knownLanguageCode + ',target:' + this.config.learningLanguageCode;
        }
        log.routeUrl = location.href;
        log.rootDataPath = this.upConfig.rootDataPath || '';
        switch (window['playerName']) {
            case 'UnitPlayer':
                log.path = this.upConfig.path;
                break;
            case 'ListPlayer':
                log.path = this.upConfig.listAaid;
                break;
            case 'PracticePlayer':
            case 'HandsFreePlayer':
                if (this.upConfig.path && (this.upConfig.path as any).url) {
                    log.path = (this.upConfig.path as any).url;
                } else if (this.upConfig.listIds) {
                    log.path = this.upConfig.listIds.join(',');
                } else if (this.upConfig.lessonIds) {
                    log.path = this.upConfig.lessonIds.join(',');
                }
                break;
            default:
                break;
        }
        this.cleanSub();
        this.httpSub = this.getCWVersion()
            .pipe(mergeMap((version: number) => {
                log.version = version;
                return this.http.post(`${this.baseApiUrl}/api/logs`, JSON.stringify(log));
            })).subscribe({next: this.cleanSub.bind(this), error: this.cleanSub.bind(this)});
    }

    /**
     *
     */
    private getLogData(message = 'CW3 error', data = 'CW3 data'): any {
        return {
            appName: 'CW3',
            version: 1,
            error: message,
            data,
            langPair: `known:${this.config.knownLanguageCode},target:${this.config.learningLanguageCode}`,
            path: this.upConfig.path,
            rootDataPath: this.upConfig.rootDataPath,
            routeUrl: window.location.href
        };
    }

    private cleanSub(): void {
        this.httpSub = cleanSubscription(this.httpSub);
    }

    /**
     * Loads cw version from package.txt
     * as of 07/09/2015 package.json
     */
    private getCWVersion(): Observable<number> {
        if (!this.locale || !this.locale.props) {
            return of(1);
        }
        if (!this.locale.props.cwversion) {
            const theBase: any = window.document.getElementsByTagName('base')[0];
            let base = theBase ? theBase.href || '' : '';
            if (base.lastIndexOf('.html') !== -1) {
                const arr = base.split('/');
                arr.splice(-1);
                base = arr.join('/');
                base = base.replace(/\/+$/, '') + '/';
            }
            return this.http.get(base + 'package.json')
                .pipe(catchError(() => of(null)),
                    map((data: any) => {
                        if (data) {
                            this.locale.props.cwversion = data.version;
                            return data.version;
                        }
                        return 1;
                    }));
        } else {
            return of(this.locale.props.cwversion);
        }
    }
}
